import { Action, Reducer, ActionCreator, combineReducers } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { batch } from 'react-redux';
import { DataManager, Query } from '@syncfusion/ej2-data';

import { RootState } from '..';
import { ILinkedinDetailsState } from '../../interfaces/states/ILinkedinDetailsState';
import { ILinkedin, ILinkedinDto } from '../../common/model/linkedin.model';
import { IODataService } from '../../services';
import * as linkedinUtils from '../../utils/linkedin.utils';

export enum Type {
  SET_LINKEDINS = '@@linkedinDetails/SET_LINKEDINS',
  SET_READMORE = '@@linkedinDetails/SET_READMORE',
  SET_SPINNERREQUESTMOREDATA = '@@linkedinDetails/SET_SPINNERREQUESTMOREDATA',
  SET_NUMPAGES = '@@linkedinDetails/SET_NUMPAGES',
  SET_CURRENTPAGE = '@@linkedinDetails/SET_CURRENTPAGE'
}

const initialState: ILinkedinDetailsState = {
  linkedins: []
};

// Actions

export type Actions = SetLinkedinsAction | SetReadMoreAction | SetSpinnerRequestMoreDataAction | SetNumPagesAction | SetCurrentPageAction;

export interface SetLinkedinsAction extends Action<Type.SET_LINKEDINS> {
  linkedins: ILinkedin[];
}

export interface SetReadMoreAction extends Action<Type.SET_READMORE> {
  linkedin: ILinkedin;
}

export interface SetSpinnerRequestMoreDataAction extends Action<Type.SET_SPINNERREQUESTMOREDATA> {
  linkedinId: string;
  showSpinner: boolean;
}

export interface SetNumPagesAction extends Action<Type.SET_NUMPAGES> {
  linkedinId: string;
  linkedinMediaId: string;
  numPages: number;
}

export interface SetCurrentPageAction extends Action<Type.SET_CURRENTPAGE> {
  linkedinId: string;
  linkedinMediaId: string;
  pageNumber: number;
}

export const setLinkedins: ActionCreator<SetLinkedinsAction> = (linkedinsLocal: ILinkedin[]) => ({
  type: Type.SET_LINKEDINS,
  linkedins: linkedinsLocal
});

export const setReadMore: ActionCreator<SetReadMoreAction> = (linkedinLocal: ILinkedin) => ({
  type: Type.SET_READMORE,
  linkedin: linkedinLocal
});

export const setSpinnerRequestMoreData: ActionCreator<SetSpinnerRequestMoreDataAction> = (linkedinId: string, showSpinner: boolean) => ({
  type: Type.SET_SPINNERREQUESTMOREDATA,
  linkedinId: linkedinId,
  showSpinner: showSpinner
});

export const setNumPages: ActionCreator<SetNumPagesAction> = (linkedinId: string, linkedinMediaId: string, numPages: number) => ({
  type: Type.SET_NUMPAGES,
  linkedinId: linkedinId,
  linkedinMediaId: linkedinMediaId,
  numPages: numPages
});

export const setCurrentPage: ActionCreator<SetCurrentPageAction> = (linkedinId: string, linkedinMediaId: string, pageNumber: number) => ({
  type: Type.SET_CURRENTPAGE,
  linkedinId: linkedinId,
  linkedinMediaId: linkedinMediaId,
  pageNumber: pageNumber
});

export const fetchLinkedins: ActionCreator<ThunkAction<void, RootState, unknown, Actions>> = (
  oDataService: IODataService,
  linkedinsDataManager: DataManager,
  linkedinsQuery: Query
) => (
    async (dispatch) => {
      const linkedinsResponse = await oDataService.executeQueryWithCount<ILinkedinDto>(linkedinsDataManager, linkedinsQuery);
      const linkedinsLocal = linkedinsResponse.result.map((linkedin: ILinkedinDto) => linkedinUtils.parseLinkedin(linkedin));
      batch(() => {
        dispatch(setLinkedins(linkedinsLocal));
      });
    }
);

export const readMoreToggle: ActionCreator<ThunkAction<void, RootState, unknown, Actions>> = (
  linkedin: ILinkedin
) => (
  async (dispatch) => {
    batch(() => {
      dispatch(setReadMore(linkedin));
    });
  }
);

export const spinnerRequestMoreData: ActionCreator<ThunkAction<void, RootState, unknown, Actions>> = (
  linkedinId: string,
  showSpinner: boolean
) => (
  async (dispatch) => {
    dispatch(setSpinnerRequestMoreData(linkedinId, showSpinner));
  }
);

export const setNumPagesToDocument: ActionCreator<ThunkAction<void, RootState, unknown, Actions>> = (
  linkedinId: string,
  linkedInMediaId: string,
  numPages: number
) => (
  async (dispatch) => {
    batch(() => {
      dispatch(setNumPages(linkedinId, linkedInMediaId, numPages));
    });
  }
);

export const setCurrentPageToDocument: ActionCreator<ThunkAction<void, RootState, unknown, Actions>> = (
  linkedinId: string,
  linkedInMediaId: string,
  pageNumber: number
) => (
  async (dispatch) => {
    batch(() => {
      dispatch(setCurrentPage(linkedinId, linkedInMediaId, pageNumber));
    });
  }
);

// Reducers

const linkedins: Reducer<ILinkedin[]> = (state = initialState.linkedins, action) => {
  switch (action.type) {
    case Type.SET_LINKEDINS:
      return (action as SetLinkedinsAction).linkedins;
    case Type.SET_READMORE: {
      const newLinkedins = state.map((linkedin) => {
        if (linkedin.id === (action as SetReadMoreAction).linkedin.id) {
          return {
            ...linkedin,
            readMore: !linkedin.readMore
          };
        }

        return linkedin;
      });

      return newLinkedins;
    }
    case Type.SET_SPINNERREQUESTMOREDATA: {
      const newLinkedins = state.map((linkedin) => {
        if (linkedin.id === (action as SetSpinnerRequestMoreDataAction).linkedinId) {
          return {
            ...linkedin,
            showSpinner: (action as SetSpinnerRequestMoreDataAction).showSpinner
          };
        }

        return linkedin;
      });

      return newLinkedins;
    }
    case Type.SET_NUMPAGES: {
      const linkedinIdLocal: string = (action as SetNumPagesAction).linkedinId;
      const linkedinMediaIdLocal: string = (action as SetNumPagesAction).linkedinMediaId;
      const numPageLocal: number = (action as SetNumPagesAction).numPages;

      const newLinkedins = state.map((linkedin) => {
        if (linkedin.id == linkedinIdLocal) {
          return {
            ...linkedin,
            medias: linkedin.medias.map((linkedinMedia) => {
              if (linkedinMedia.id == linkedinMediaIdLocal) {
                return {
                  ...linkedinMedia,
                  numPages: numPageLocal
                };
              }

              return linkedinMedia;
            })
          };
        }

        return linkedin;
      });

      return newLinkedins;
    }
    case Type.SET_CURRENTPAGE: {
      const linkedinIdLocal: string = (action as SetCurrentPageAction).linkedinId;
      const linkedinMediaIdLocal: string = (action as SetCurrentPageAction).linkedinMediaId;
      const pageNumberLocal: number = (action as SetCurrentPageAction).pageNumber;

      const newLinkedins = state.map((linkedin) => {
        if (linkedin.id == linkedinIdLocal) {
          return {
            ...linkedin,
            medias: linkedin.medias.map((linkedinMedia) => {
              if (linkedinMedia.id == linkedinMediaIdLocal) {
                return {
                  ...linkedinMedia,
                  pageNumber: pageNumberLocal
                };
              }

              return linkedinMedia;
            })
          };
        }

        return linkedin;
      });

      return newLinkedins;
    }
    default:
      return state;
  }
};

export const linkedinDetailsStore = combineReducers({
  linkedins
});
